import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  Target,
  Zap,
  Database,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Calendar,
  Lightbulb,
  ScanBarcode,
  Layers,
  Camera,
  LineChart,
  Video,
  Bluetooth,
  Download,
  FileText,
  FileSpreadsheet
} from 'lucide-react';
import api from '../../../utils/api';
import { useLanguage } from '../../../contexts/LanguageContext';
import { toast } from 'sonner';
import AnalyticsTrendCharts from './AnalyticsTrendCharts';

/**
 * ScanAnalyticsDashboard - Phase 15 Component
 * 
 * Displays DRLP scanning insights and efficiency metrics.
 */
export default function ScanAnalyticsDashboard() {
  const { t } = useLanguage();
  const [showTrendCharts, setShowTrendCharts] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [efficiency, setEfficiency] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(7);
  const [exporting, setExporting] = useState(false);

  // Export analytics to CSV
  const handleExportCSV = useCallback(async () => {
    setExporting(true);
    try {
      const response = await api.get(`/barcode/analytics/export?days=${selectedPeriod}&format=csv`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `scan_analytics_${selectedPeriod}days_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('analytics.export_success', 'Analytics exported successfully!'));
    } catch (err) {
      console.error('Export failed:', err);
      toast.error(t('analytics.export_failed', 'Failed to export analytics'));
    } finally {
      setExporting(false);
    }
  }, [selectedPeriod, t]);

  // Export analytics to JSON (for PDF-like summary)
  const handleExportJSON = useCallback(async () => {
    setExporting(true);
    try {
      const response = await api.get(`/barcode/analytics/export?days=${selectedPeriod}&format=json`);
      
      // Create formatted JSON download
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `scan_analytics_summary_${selectedPeriod}days_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('analytics.export_success', 'Analytics exported successfully!'));
    } catch (err) {
      console.error('Export failed:', err);
      toast.error(t('analytics.export_failed', 'Failed to export analytics'));
    } finally {
      setExporting(false);
    }
  }, [selectedPeriod, t]);

  // Export analytics to PDF (Summary Report)
  const handleExportPDF = useCallback(async () => {
    setExporting(true);
    try {
      const response = await api.get(`/barcode/analytics/export?days=${selectedPeriod}&format=pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `scan_analytics_report_${selectedPeriod}days_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('analytics.export_success', 'PDF report exported successfully!'));
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error(t('analytics.export_failed', 'Failed to export PDF report'));
    } finally {
      setExporting(false);
    }
  }, [selectedPeriod, t]);

  // Fetch all analytics data
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [statsRes, efficiencyRes, summaryRes] = await Promise.all([
        api.get(`/barcode/analytics/stats?days=${selectedPeriod}`),
        api.get(`/barcode/analytics/efficiency?days=${selectedPeriod}`),
        api.get('/barcode/analytics/weekly-summary')
      ]);
      
      setStats(statsRes.data);
      setEfficiency(efficiencyRes.data);
      setWeeklySummary(summaryRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Get trend icon
  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  // Get mode icon
  const getModeIcon = (mode) => {
    const icons = {
      single: <ScanBarcode className="w-4 h-4" />,
      continuous: <Zap className="w-4 h-4" />,
      photo: <Camera className="w-4 h-4" />,
      multi_image: <Layers className="w-4 h-4" />,
      video: <Video className="w-4 h-4" />,
      scanner: <Bluetooth className="w-4 h-4" />,
      batch: <Layers className="w-4 h-4" />
    };
    return icons[mode] || <ScanBarcode className="w-4 h-4" />;
  };

  // Get source color
  const getSourceColor = (source) => {
    const colors = {
      cache: 'bg-green-100 text-green-800',
      store_registry: 'bg-blue-100 text-blue-800',
      openfoodfacts: 'bg-purple-100 text-purple-800',
      upcitemdb: 'bg-orange-100 text-orange-800',
      not_found: 'bg-red-100 text-red-800'
    };
    return colors[source] || 'bg-gray-100 text-gray-800';
  };

  // Render headline metrics
  const renderHeadlineMetrics = () => {
    if (!weeklySummary) return null;
    
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Total Scans */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('analytics.total_scans', 'Total Scans')}</p>
                <p className="text-3xl font-bold">{weeklySummary.total_scans || 0}</p>
              </div>
              <div className="flex flex-col items-end">
                {getTrendIcon(weeklySummary.wow_trend)}
                <span className={`text-xs ${weeklySummary.wow_scan_change > 0 ? 'text-green-600' : weeklySummary.wow_scan_change < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                  {weeklySummary.wow_scan_change > 0 ? '+' : ''}{weeklySummary.wow_scan_change}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Success Rate */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('analytics.success_rate', 'Success Rate')}</p>
                <p className="text-3xl font-bold">{weeklySummary.success_rate || 0}%</p>
              </div>
              <Target className={`w-8 h-8 ${weeklySummary.success_rate > 80 ? 'text-green-500' : weeklySummary.success_rate > 60 ? 'text-yellow-500' : 'text-red-500'}`} />
            </div>
            <Progress value={weeklySummary.success_rate || 0} className="h-1 mt-2" />
          </CardContent>
        </Card>

        {/* RSHDs Created */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('analytics.rshds_created', 'RSHDs Created')}</p>
                <p className="text-3xl font-bold">{weeklySummary.rshds_created || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        {/* Items/Hour */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('analytics.items_per_hour', 'Items/Hour')}</p>
                <p className="text-3xl font-bold">{weeklySummary.items_per_hour || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render source distribution
  const renderSourceDistribution = () => {
    if (!stats?.source_distribution) return null;
    
    const sources = Object.entries(stats.source_distribution);
    const total = sources.reduce((sum, [_, count]) => sum + count, 0);
    
    if (total === 0) {
      return (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Database className="w-5 h-5" />
              {t('analytics.data_sources', 'Data Sources')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500 text-center py-4">
              {t('analytics.no_data', 'No scan data yet. Start scanning to see insights!')}
            </p>
          </CardContent>
        </Card>
      );
    }
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Database className="w-5 h-5" />
            {t('analytics.data_sources', 'Data Sources')}
          </CardTitle>
          <CardDescription>
            {t('analytics.where_data_comes', 'Where your product data comes from')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {sources.map(([source, count]) => {
            const percentage = Math.round((count / total) * 100);
            return (
              <div key={source} className="space-y-1">
                <div className="flex items-center justify-between">
                  <Badge className={getSourceColor(source)}>
                    {source.replace('_', ' ').toUpperCase()}
                  </Badge>
                  <span className="text-sm font-medium">{count} ({percentage}%)</span>
                </div>
                <Progress value={percentage} className="h-2" />
              </div>
            );
          })}
        </CardContent>
      </Card>
    );
  };

  // Render mode distribution
  const renderModeDistribution = () => {
    if (!stats?.mode_distribution) return null;
    
    const modes = Object.entries(stats.mode_distribution);
    const total = modes.reduce((sum, [_, count]) => sum + count, 0);
    
    if (total === 0) return null;
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {t('analytics.scan_modes', 'Scan Modes Used')}
          </CardTitle>
          <CardDescription>
            {t('analytics.how_you_scan', 'How you scan products')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {modes.map(([mode, count]) => {
              const percentage = Math.round((count / total) * 100);
              const isTopMode = mode === weeklySummary?.most_used_mode;
              
              return (
                <div 
                  key={mode} 
                  className={`p-3 rounded-lg border ${isTopMode ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {getModeIcon(mode)}
                    <span className="text-sm font-medium capitalize">{mode.replace('_', ' ')}</span>
                    {isTopMode && <Badge variant="secondary" className="text-xs">Top</Badge>}
                  </div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs text-gray-500">{percentage}% of scans</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render daily breakdown chart (simple bar visualization)
  const renderDailyBreakdown = () => {
    if (!stats?.daily_breakdown?.length) return null;
    
    const maxScans = Math.max(...stats.daily_breakdown.map(d => d.total));
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            {t('analytics.daily_activity', 'Daily Activity')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {stats.daily_breakdown.map((day, idx) => {
              const width = maxScans > 0 ? (day.total / maxScans) * 100 : 0;
              const date = new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
              
              return (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-24">{date}</span>
                  <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-400 flex items-center justify-end px-2 transition-all"
                      style={{ width: `${width}%` }}
                    >
                      {day.total > 0 && (
                        <span className="text-xs text-white font-medium">{day.total}</span>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 w-12">
                    {day.successful}/{day.total}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render recommendations
  const renderRecommendations = () => {
    if (!weeklySummary?.recommendations?.length) return null;
    
    return (
      <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
            <Lightbulb className="w-5 h-5" />
            {t('analytics.recommendations', 'Tips to Improve')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {weeklySummary.recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-amber-900">
                <span className="text-amber-500 mt-0.5">•</span>
                {rec}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    );
  };

  // Render efficiency metrics
  const renderEfficiencyMetrics = () => {
    if (!efficiency) return null;
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="w-5 h-5" />
            {t('analytics.efficiency', 'Efficiency Metrics')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-700">{efficiency.cache_hit_rate || 0}%</p>
              <p className="text-xs text-green-600">{t('analytics.cache_hit_rate', 'Cache Hit Rate')}</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-700">{efficiency.conversion_rate || 0}%</p>
              <p className="text-xs text-blue-600">{t('analytics.conversion_rate', 'Conversion Rate')}</p>
            </div>
            {efficiency.most_efficient_mode && (
              <div className="col-span-2 text-center p-3 bg-purple-50 rounded-lg">
                <p className="text-sm text-purple-600">{t('analytics.best_mode', 'Most Efficient Mode')}</p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  {getModeIcon(efficiency.most_efficient_mode)}
                  <span className="font-semibold text-purple-700 capitalize">
                    {efficiency.most_efficient_mode.replace('_', ' ')}
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6" data-testid="scan-analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">{t('analytics.title', 'Scan Analytics')}</h2>
          <p className="text-sm text-gray-500">
            {t('analytics.subtitle', 'Your scanning performance and insights')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle: Summary vs Charts */}
          <Button
            variant={showTrendCharts ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowTrendCharts(!showTrendCharts)}
            data-testid="toggle-charts-btn"
          >
            <LineChart className="w-4 h-4 mr-1.5" />
            {showTrendCharts ? 'Summary' : 'Charts'}
          </Button>
          
          <Tabs value={selectedPeriod.toString()} onValueChange={(v) => setSelectedPeriod(parseInt(v))}>
            <TabsList>
              <TabsTrigger value="7">7 {t('analytics.days', 'days')}</TabsTrigger>
              <TabsTrigger value="14">14 {t('analytics.days', 'days')}</TabsTrigger>
              <TabsTrigger value="30">30 {t('analytics.days', 'days')}</TabsTrigger>
            </TabsList>
          </Tabs>
          
          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={exporting} data-testid="export-btn">
                {exporting ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="ml-1.5 hidden sm:inline">{t('analytics.export', 'Export')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportCSV} data-testid="export-csv">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {t('analytics.export_csv', 'Export as CSV')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPDF} data-testid="export-pdf">
                <FileText className="w-4 h-4 mr-2 text-red-500" />
                {t('analytics.export_pdf', 'Export as PDF Report')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportJSON} data-testid="export-json">
                <FileText className="w-4 h-4 mr-2" />
                {t('analytics.export_json', 'Export as JSON')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button variant="outline" size="icon" onClick={fetchAnalytics}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Trend Charts View */}
      {showTrendCharts ? (
        <AnalyticsTrendCharts />
      ) : (
        <>
          {/* Summary View - Original Content */}

      {/* Headline Metrics */}
      {renderHeadlineMetrics()}

      {/* Main Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {renderSourceDistribution()}
        {renderModeDistribution()}
        {renderDailyBreakdown()}
        {renderEfficiencyMetrics()}
      </div>

      {/* Recommendations */}
      {renderRecommendations()}
        </>
      )}
    </div>
  );
}
